import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import React, { useState, type JSX } from 'react';
import { DateiKategorie, Unterzeichner } from '../shared/constants';
import { toDateTimeString } from '../shared/dateTime';
import { Datei, Foto, Zahlungseingang } from '../types';
import { Euro } from './common/Euro';
import { S3Link } from './common/S3Link';
import { DokumentLoeschenBestaetigungsdialog } from './dialog/DokumentLoeschenBestaetigungsdialog';
import { KategorieEditierenDialog } from './dialog/KategorieEditierenDialog';
import { WertDialog } from './dialog/WertDialog';
import Tabelle from './Tabelle/Tabelle';
import { enqueueSnackbar } from 'notistack';

const PDF_DOKUMENTKATEGORIEN = [DateiKategorie.RECHNUNG, DateiKategorie.UNTERSCHRIFTEN, DateiKategorie.WEBSCAN];

const DOKUMENTKATEGORIEN = [
  DateiKategorie.ANGEBOT,
  DateiKategorie.RECHNUNG,
  DateiKategorie.HIS,
  DateiKategorie.FREMDRECHNUNG,
  DateiKategorie.UNTERSCHRIFTEN,
  DateiKategorie.WEBSCAN,
  DateiKategorie.LACKSCHICHTENDICKENMESSUNG,
  DateiKategorie.GUTSCHRIFT,
  DateiKategorie.STORNO
];

type Props = {
  readonly dokumente: Datei[];
  readonly spalten: Array<keyof Datei>;
  readonly handleDokumentLoeschen?: (dokument: Datei) => void;
  readonly handleKategorieHinzufuegen?: (dokument: Datei, kategorie: string, wert?: number | null, unterzeichner?: Unterzeichner) => void;
  readonly handleKategorieLoeschen?: (foto: Foto | Datei, kategorie: string) => void;
  readonly generiereStornoDokument?: (zuRechnungsnummer: string) => void;
  readonly generiereGutschriftDokument?: (gutschriftWert: string, zuRechnungsnummer: string) => void;
  readonly zahlungseingaenge?: Zahlungseingang[];
  readonly darfKategorisieren?: boolean;
  readonly darfRechnungKategorie?: boolean;
  readonly darfLoeschen?: boolean;
  readonly gutschriftStoroMenu?: boolean;
  readonly dataTestId?: string;
};

export function Dokumententabelle({
  dokumente,
  spalten,
  handleDokumentLoeschen,
  handleKategorieHinzufuegen,
  handleKategorieLoeschen,
  generiereStornoDokument,
  generiereGutschriftDokument,
  dataTestId,
  zahlungseingaenge,
  darfKategorisieren = false,
  darfRechnungKategorie = false,
  darfLoeschen = false,
  gutschriftStoroMenu = false
}: Props): JSX.Element {
  const [dokument, setDokument] = useState<Datei | null>(null);
  const [kategorien, setKategorien] = useState<string[]>([]);
  const [dokumentLoeschenBestaetigungsdialogOpen, setDokumentLoeschenBestaetigungsdialogOpen] = useState(false);
  const [kategorieHinzufuegenDialogOpen, setKategorieHinzufuegenDialogOpen] = useState(false);
  const [wertDialogOpen, setWertDialogOpen] = useState(false);
  const [maxGutschriftWert, setMaxGutschriftWert] = useState<number | null>(null);

  const actions = [];

  if (darfKategorisieren) {
    actions.push({
      label: 'Kategorie hinzufügen',
      onClick: (zeile: Datei) => {
        setKategorien(
          DOKUMENTKATEGORIEN.filter((kategorie) => {
            const hatKategorie = zeile?.kategorien?.includes(kategorie);
            const istPdfTyp = PDF_DOKUMENTKATEGORIEN.includes(kategorie);
            const endetNichtMitPdf = !zeile?.dateiname?.toLowerCase().endsWith('.pdf');
            if (kategorie === DateiKategorie.RECHNUNG && !darfRechnungKategorie) {
              return false;
            }
            return !(hatKategorie ?? (istPdfTyp && endetNichtMitPdf));
          })
        );

        setDokument(zeile);
        setKategorieHinzufuegenDialogOpen(true);
      },
      disabled: false,
      hidden: (zeile: Datei) => !zeile.key
    });
  }

  if (darfLoeschen) {
    actions.push({
      label: 'Löschen',
      onClick: (zeile: Datei) => {
        setDokument(zeile);
        setDokumentLoeschenBestaetigungsdialogOpen(true);
      },
      disabled: false
    });
  }

  if (gutschriftStoroMenu) {
    actions.push({
      label: 'Gutschrift erstellen',
      onClick: (zeile: Datei) => {
        if (zeile.kategorien?.includes(DateiKategorie.RECHNUNG)) {
          setDokument(zeile);
          const zurRechnung = zahlungseingaenge?.filter((z) => z.rechnungsnummer === zeile.dateiname?.replace('.pdf', ''));
          const sum = zurRechnung?.reduce((acc, curr) => acc + curr.betrag, 0) ?? 0;
          const rechnungswert = zeile.wert ? Number(zeile.wert) : 0;
          setMaxGutschriftWert(Math.round((rechnungswert * 1.19 - sum) * 100) / 100);
          setWertDialogOpen(true);
        } else {
          enqueueSnackbar('Eine Gutschrift kann nur für Rechnungen erstellt werden.');
        }
      },
      disabled: false
    });
  }

  if (gutschriftStoroMenu) {
    actions.push({
      label: 'Storno erstellen',
      onClick: (zeile: Datei) => {
        if (zeile.kategorien?.includes(DateiKategorie.RECHNUNG)) {
          if (generiereStornoDokument) {
            generiereStornoDokument(zeile.dateiname?.replace('.pdf', '') ?? '');
          }
        } else {
          enqueueSnackbar('Ein Storno kann nur für Rechnungen erstellt werden.');
        }
      },
      disabled: false
    });
  }

  const columns = [];

  if (spalten.includes('dateiname')) {
    columns.push({
      label: 'Dateiname',
      render: (zeile: Datei) => {
        return zeile.key ? (
          <S3Link s3key={zeile.key ?? ''} underline="always" target="_blank" color="primary">
            {zeile.dateiname}
          </S3Link>
        ) : (
          zeile.dateiname
        );
      }
    });
  }
  if (spalten.includes('uploaddatum')) {
    columns.push({ label: 'Datum', render: (zeile: Datei) => toDateTimeString(zeile.uploaddatum) });
  }
  if (spalten.includes('kategorien')) {
    columns.push({
      label: 'Kategorie',
      render: (zeile: Datei) => (
        <>
          {zeile.kategorien?.map((kategorie) => (
            <Chip
              key={kategorie}
              label={kategorie}
              color="primary"
              size="small"
              onDelete={darfLoeschen && !zeile.restwertangebot ? () => handleKategorieLoeschen && handleKategorieLoeschen(zeile, kategorie) : undefined}
            />
          ))}
          {zeile.restwertangebot ? <Box color="error.main">Restwertangebote sind nicht löschbar</Box> : null}
        </>
      )
    });
  }
  if (spalten.includes('wert')) {
    columns.push({ label: 'Nettowert', id: 'betrag', render: (zeile: Datei) => zeile.wert && <Euro value={Number(zeile.wert)}></Euro> });
  }

  return (
    <>
      <Tabelle actions={actions} columns={columns} tableData={dokumente} dataTestId={dataTestId} />

      <DokumentLoeschenBestaetigungsdialog
        open={dokumentLoeschenBestaetigungsdialogOpen}
        onClose={() => {
          setDokumentLoeschenBestaetigungsdialogOpen(false);
          setDokument(null);
        }}
        onDelete={() => {
          handleDokumentLoeschen && dokument && handleDokumentLoeschen(dokument);
          setDokument(null);
        }}
      />

      <KategorieEditierenDialog
        open={kategorieHinzufuegenDialogOpen}
        onClose={() => setKategorieHinzufuegenDialogOpen(false)}
        onEdit={(kategorie: string, wert?: number | null, unterzeichner?: Unterzeichner) => {
          setKategorieHinzufuegenDialogOpen(false);
          handleKategorieHinzufuegen && dokument && handleKategorieHinzufuegen(dokument, kategorie, wert, unterzeichner);
        }}
        kategorien={kategorien}
        text={'Bitte wählen Sie eine Kategorie aus, die dem Dokument hinzugefügt werden soll.'}
        titel="Kategorie hinzufügen"
        saveButtonText="Hinzufügen"
      />

      <WertDialog
        open={wertDialogOpen}
        onClose={() => {
          setWertDialogOpen(false);
          setDokument(null);
        }}
        maxWert={maxGutschriftWert ?? undefined}
        onSave={(gutschriftWert) => {
          if (generiereGutschriftDokument && gutschriftWert) {
            generiereGutschriftDokument(gutschriftWert.toString(), dokument?.dateiname?.replace('.pdf', '') ?? '');
          }
          setWertDialogOpen(false);
          setDokument(null);
        }}
        text="Tragen Sie den Gutschriftwert ein:"
        titel="Gutschrift"
        saveButtonText="Speichern"
      />
    </>
  );
}
